import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  Star,
  User,
  Briefcase,
  MessageSquare,
  ArrowRight,
  Ban,
  CalendarDays,
  ShieldCheck
} from 'lucide-react';

export const ApprovalCenter = ({ embedded = false }) => {
  const { user, refreshProfile } = useAuth();
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [activePlacements, setActivePlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectMessage, setRejectMessage] = useState('');
  const [processing, setProcessing] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [revoking, setRevoking] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const focusRequestId = searchParams.get('request');
  const cardRefs = useRef({});

  useEffect(() => {
    fetchApprovals();
  }, []);

  // When ApprovalCenter is loaded via ?request=<bid_id> (e.g. from an email
  // "Review Request" link or a notification click), scroll to and highlight
  // the matching request card once data has loaded.
  useEffect(() => {
    if (!focusRequestId || loading) return;
    const el = cardRefs.current[focusRequestId];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-emerald-500', 'ring-offset-2');
      setTimeout(() => {
        el.classList.remove('ring-2', 'ring-emerald-500', 'ring-offset-2');
        // Clear the query param after highlighting so refreshes look clean
        const next = new URLSearchParams(searchParams);
        next.delete('request');
        setSearchParams(next, { replace: true });
      }, 4000);
    } else if (pendingApprovals.length > 0) {
      // Request not in pending list — either already processed or invalid
      toast.info('This request may have already been responded to or expired.');
      const next = new URLSearchParams(searchParams);
      next.delete('request');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, focusRequestId, pendingApprovals.length]);

  const fetchApprovals = async () => {
    try {
      const [approvalsData, placementsData] = await Promise.all([
        api.getPendingApprovals(),
        api.getActivePlacements()
      ]);
      setPendingApprovals(approvalsData.pending || []);
      setActivePlacements(placementsData.placements || []);
    } catch (error) {
      console.error('Failed to fetch approvals:', error);
      toast.error('Failed to load approval requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (request) => {
    setProcessing(true);
    try {
      const result = await api.respondToApproval(request.id, true, '');
      toast.success(`Approved! You earned $${result.earnings?.toFixed(2) || '10.00'}`);
      fetchApprovals();
      refreshProfile();
    } catch (error) {
      console.error('Approval failed:', error);
      toast.error('Failed to approve request');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    
    setProcessing(true);
    try {
      await api.respondToApproval(selectedRequest.id, false, rejectMessage);
      toast.success('Request rejected');
      setRejectDialogOpen(false);
      setSelectedRequest(null);
      setRejectMessage('');
      fetchApprovals();
    } catch (error) {
      console.error('Rejection failed:', error);
      toast.error('Failed to reject request');
    } finally {
      setProcessing(false);
    }
  };

  const handleRevoke = async (placementId) => {
    if (!window.confirm('Are you sure you want to revoke this ad placement? The slot will become available again.')) return;
    setRevoking(placementId);
    try {
      const result = await api.revokePlacement(placementId);
      toast.success(result.message || 'Placement revoked');
      fetchApprovals();
      refreshProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke placement');
    } finally {
      setRevoking(null);
    }
  };

  if (loading) {
    return embedded ? (
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/4" />
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-32 bg-muted rounded-lg" />
            ))}
          </div>
        </div>
    ) : (
      <Layout>
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/4" />
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-32 bg-muted rounded-lg" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  const content = (
      <div className="space-y-6" data-testid="approval-center">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit'] tracking-tight">Approval Center</h1>
          <p className="text-muted-foreground mt-1">
            Review and manage ad requests for your signatures
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-border/50">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{pendingApprovals.length}</p>
                <p className="text-sm text-muted-foreground">Pending Requests</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  ${pendingApprovals.reduce((sum, req) => sum + (req.bid_amount * 0.5), 0).toFixed(2)}
                </p>
                <p className="text-sm text-muted-foreground">Potential Earnings</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                <Star className="w-6 h-6 text-accent" />
              </div>
              <div>
                <p className="text-2xl font-bold">{user?.reputation_score?.toFixed(1) || '0.0'}</p>
                <p className="text-sm text-muted-foreground">Your Reputation</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Requests */}
        {pendingApprovals.length === 0 ? (
          <Card className="border-border/50 border-dashed">
            <CardContent className="p-8 text-center">
              <div className="w-12 h-12 bg-muted rounded-lg mx-auto mb-4 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-muted-foreground" />
              </div>
              <h3 className="font-medium mb-2">All caught up!</h3>
              <p className="text-sm text-muted-foreground">
                No pending approval requests at the moment
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {pendingApprovals.map((request) => (
              <Card
                key={request.id}
                ref={el => { if (el) cardRefs.current[request.id] = el; }}
                className="border-border/50 transition-shadow"
                data-testid={`request-${request.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                    {/* Bidder Info */}
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-accent rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-xl">
                          {request.bidder?.name?.charAt(0) || 'U'}
                        </span>
                      </div>
                      <div className="space-y-1">
                        <p className="font-semibold text-lg">{request.bidder?.name || 'Unknown'}</p>
                        <p className="text-sm text-accent">{request.bidder?.profession || 'Professional'}</p>
                        {request.bidder?.company && (
                          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                            <Briefcase className="w-3.5 h-3.5" />
                            {request.bidder.company}
                          </div>
                        )}
                        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          <Star className="w-3.5 h-3.5 text-yellow-500" />
                          {request.bidder?.reputation_score?.toFixed(1) || '0.0'} reputation
                        </div>
                      </div>
                    </div>

                    {/* Bid Details */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 lg:gap-8">
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Bid Amount</p>
                        <p className="text-2xl font-bold text-green-600">${request.bid_amount}</p>
                        <p className="text-xs text-muted-foreground">
                          You earn: ${(request.bid_amount * 0.5).toFixed(2)}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Ad Slot</p>
                        <Badge variant="secondary" className="text-lg px-3 py-1">
                          #{request.position}
                        </Badge>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3">
                      <Button
                        onClick={() => handleApprove(request)}
                        disabled={processing}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid={`approve-btn-${request.id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Approve
                      </Button>
                      <Dialog open={rejectDialogOpen && selectedRequest?.id === request.id} onOpenChange={(open) => {
                        setRejectDialogOpen(open);
                        if (!open) {
                          setSelectedRequest(null);
                          setRejectMessage('');
                        }
                      }}>
                        <DialogTrigger asChild>
                          <Button
                            variant="outline"
                            onClick={() => setSelectedRequest(request)}
                            data-testid={`reject-btn-${request.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Reject
                          </Button>
                        </DialogTrigger>
                        <DialogContent data-testid="reject-dialog">
                          <DialogHeader>
                            <DialogTitle>Reject Ad Request</DialogTitle>
                            <DialogDescription>
                              Are you sure you want to reject this request from {request.bidder?.name}?
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="space-y-2">
                              <label className="text-sm font-medium">Reason (Optional)</label>
                              <Textarea
                                placeholder="Let them know why you're rejecting..."
                                value={rejectMessage}
                                onChange={(e) => setRejectMessage(e.target.value)}
                                rows={3}
                                data-testid="reject-message-input"
                              />
                            </div>
                          </div>
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
                              Cancel
                            </Button>
                            <Button
                              variant="destructive"
                              onClick={handleReject}
                              disabled={processing}
                              data-testid="confirm-reject-btn"
                            >
                              {processing ? 'Rejecting...' : 'Confirm Reject'}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>

                  {/* Message from bidder */}
                  {request.message && (
                    <div className="mt-4 pt-4 border-t border-border/50">
                      <div className="flex items-start gap-2">
                        <MessageSquare className="w-4 h-4 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-1">Message:</p>
                          <p className="text-sm">{request.message}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Info Card */}
        <Card className="border-border/50 bg-accent/5">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <DollarSign className="w-5 h-5 text-accent" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">How Revenue Sharing Works</h3>
                <p className="text-sm text-muted-foreground">
                  When you approve an ad request, you earn 50% of the bid amount immediately. 
                  The advertiser's information will be added to your email signature, giving them 
                  exposure with every email you send. You maintain full control over who advertises 
                  on your signature.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Advertisers Section */}
        {activePlacements.length > 0 && (
          <div className="space-y-4" data-testid="active-advertisers-section">
            <h2 className="text-xl font-bold font-['Outfit'] flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-green-600" />
              Active Advertisers ({activePlacements.length})
            </h2>
            <p className="text-sm text-muted-foreground">
              Currently advertising on your email signatures. You can revoke after the 30-day commitment period.
            </p>
            <div className="space-y-3">
              {activePlacements.map((placement) => {
                const startDate = placement.commitment_start ? new Date(placement.commitment_start) : null;
                const endDate = placement.commitment_end ? new Date(placement.commitment_end) : null;
                const daysLeft = endDate ? Math.max(0, Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24))) : 0;
                return (
                  <Card key={placement.id} className={`border-border/50 ${placement.can_revoke ? 'border-l-4 border-l-amber-400' : 'border-l-4 border-l-green-400'}`} data-testid={`active-placement-${placement.id}`}>
                    <CardContent className="p-5">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div className="flex items-center gap-4">
                          {placement.advertiser.profile_photo ? (
                            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-green-200 flex-shrink-0">
                              <img src={placement.advertiser.profile_photo} alt="" className="w-full h-full object-cover" />
                            </div>
                          ) : (
                            <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-white font-bold text-lg">{placement.advertiser.initial}</span>
                            </div>
                          )}
                          <div>
                            <p className="font-semibold">{placement.advertiser.name}</p>
                            <p className="text-sm text-accent">{placement.advertiser.profession}</p>
                            {placement.advertiser.company && (
                              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                <Briefcase className="w-3 h-3" />
                                {placement.advertiser.company}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-4">
                          <div className="text-center">
                            <Badge variant="secondary" className="text-sm px-3 py-1">Slot #{placement.position}</Badge>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-muted-foreground">Earning</p>
                            <p className="text-sm font-bold text-green-600">${((placement.amount_paid || 20) * 0.5).toFixed(0)}/mo</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-muted-foreground">Commitment</p>
                            <div className="flex items-center gap-1 text-xs">
                              <CalendarDays className="w-3 h-3" />
                              {startDate ? startDate.toLocaleDateString() : '?'} — {endDate ? endDate.toLocaleDateString() : '?'}
                            </div>
                          </div>
                          <div>
                            {placement.can_revoke ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => handleRevoke(placement.id)}
                                disabled={revoking === placement.id}
                                data-testid={`revoke-btn-${placement.id}`}
                              >
                                <Ban className="w-3.5 h-3.5 mr-1.5" />
                                {revoking === placement.id ? 'Revoking...' : 'Revoke'}
                              </Button>
                            ) : (
                              <Badge variant="outline" className="text-xs text-green-600 border-green-200">
                                <Clock className="w-3 h-3 mr-1" />
                                {daysLeft} days left
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      </div>
  );

  return embedded ? content : <Layout>{content}</Layout>;
};

export default ApprovalCenter;
